import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MicIcon, HomeIcon, VideoIcon, CreditCardIcon, UserIcon, MenuIcon, XIcon, ClockIcon } from 'lucide-react';
import { useAPIKey } from '../../contexts/APIKeyContext';
import { useAuth } from '../../contexts/AuthContext';

const Navbar: React.FC = () => {
  const location = useLocation();
  const { apiKey } = useAPIKey();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial scroll position

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Base navigation items
  const baseNavigation = [
    { name: 'Home', href: '/app', icon: HomeIcon },
    { name: 'Session', href: '/session', icon: VideoIcon },
    { name: 'History', href: '/history', icon: ClockIcon },
    { name: 'Account', href: '/account', icon: UserIcon },
  ];

  // Add subscription tab only if user exists and not in BYOK mode
  const navigation = user ? [
    ...baseNavigation,
    ...(apiKey ? [] : [{ name: 'Subscription', href: '/subscription', icon: CreditCardIcon }])
  ] : [];

  return (
    <nav className={`
      bg-primary-900/95 backdrop-blur-sm border-b border-white/10
      sticky top-0 z-40 transition-shadow duration-200
      ${scrolled ? 'shadow-md' : 'shadow-sm'}
    `}>
      <div className="container mx-auto px-4 relative">
        <div className="flex justify-between h-16">
          <div className="flex">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <MicIcon className="h-8 w-8 text-primary-600" />
              </motion.div>
              <span className="ml-2 text-xl font-bold text-white">VoiceVision</span>
            </Link>
          </div>

          {/* Desktop menu */}
          <div className="hidden md:flex items-center space-x-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors text-white ${
                    isActive
                      ? 'bg-white/20 font-semibold'
                      : 'hover:bg-white/10'
                  }`}
                >
                  <Icon className="mr-1.5 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
            
            {!user && (
              <>
                <Link
                 aria-label="Sign in to your account"
                  to="/login"
                  className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors text-white hover:bg-white/10"
                >
                  Sign In
                </Link>
                <Link
                 aria-label="Create a new account"
                  to="/signup"
                  className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors bg-white/20 text-white hover:bg-white/30"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
             aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
             aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:bg-white/10 focus:outline-none"
            >
              {mobileMenuOpen ? (
                <XIcon className="block h-6 w-6" />
              ) : (
                <MenuIcon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <motion.div
        className="md:hidden"
        initial={false}
        animate={{ height: mobileMenuOpen ? 'auto' : 0 }}
        transition={{ duration: 0.2 }}
        style={{ overflow: 'hidden' }}
      >
        <div className="pt-2 pb-3 space-y-1 px-4">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 rounded-md text-base font-medium text-white ${
                  isActive
                    ? 'bg-white/20'
                    : 'hover:bg-white/10'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Icon className="mr-3 h-5 w-5" />
                {item.name}
              </Link>
            );
          })}
          
          {!user && (
            <>
              <Link
               aria-label="Sign in to your account"
                to="/login"
                className="flex items-center px-3 py-2 rounded-md text-base font-medium text-white hover:bg-white/10"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign In
              </Link>
              <Link
               aria-label="Create a new account"
                to="/signup"
                className="flex items-center px-3 py-2 rounded-md text-base font-medium bg-white/20 text-white hover:bg-white/30"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign Up
              </Link>
            </>
          )}
        </div>
      </motion.div>
    </nav>
  );
};

export default Navbar;